<script setup lang="ts">
import type { Component } from 'vue'
import type { InfoNoteTone } from '~/features/my-wallet/types/info-note'
import IconBlock from '~icons/material-symbols/block'
import IconInfo from '~icons/material-symbols/info-outline'
import IconLightbulb from '~icons/material-symbols/lightbulb-outline'
import IconStar from '~icons/material-symbols/star-outline'
import IconWarning from '~icons/material-symbols/warning-outline'

interface Props {
  tone: InfoNoteTone
  icon?: Component
}

const props = defineProps<Props>()

const TONE_ICON: Record<InfoNoteTone, Component> = {
  success: IconStar,
  warning: IconWarning,
  info: IconInfo,
  neutral: IconLightbulb,
  danger: IconBlock,
}

const noteIcon = computed(() => props.icon ?? TONE_ICON[props.tone])
</script>

<template>
  <div class="info-note" :class="`-${tone}`">
    <component :is="noteIcon" class="info-note-icon" aria-hidden="true" />
    <div class="info-note-text">
      <slot name="title" />
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.info-note {
  @apply flex items-center gap-2 px-4 pt-3 pb-4 rounded-md;

  &.-success {
    @apply border border-brand-secondary-medium text-feedback-positive-dark;
    @apply bg-brand-secondary-light;
  }

  &.-warning {
    @apply bg-feedback-warning-light text-feedback-warning-dark;
  }

  &.-info {
    @apply bg-feedback-informative-light border border-feedback-informative-medium text-feedback-informative-dark;

    .info-note-icon {
      @apply text-neutral-low-light;
    }
  }

  &.-neutral {
    @apply bg-neutral-high-light text-neutral-low-dark;

    .info-note-icon {
      @apply text-brand-secondary-pure;
    }
  }

  &.-danger {
    @apply bg-feedback-negative-light text-feedback-negative-dark;
  }

  .info-note-icon {
    @apply w-6 h-6 flex-shrink-0;
  }

  .info-note-text {
    @apply flex flex-col flex-1 gap-1 fonts-body-medium-regular;
  }
}
</style>
